import React, { useState } from 'react';
import { TrendingUp, TrendingDown, AlertCircle, Brain, ChevronDown, ChevronUp, X } from 'lucide-react';
import type { AnalyticsInsight } from '../../../types/analytics';

interface AnalyticsInsightsProps {
  insights: AnalyticsInsight[];
  contentName?: string;
}

export default function AnalyticsInsights({ insights, contentName }: AnalyticsInsightsProps) {
  const [showDetailedModal, setShowDetailedModal] = useState(false);

  // AI analysis function remains the same
  const getAIAnalysis = (name: string) => {
    if (!name) return null;

    const keywords = name.toLowerCase().split(' ');
    const contentType = {
      isGaming: keywords.some(k => ['minecraft', 'fortnite', 'game', 'gaming', 'survived'].includes(k)),
      isChallenge: keywords.some(k => ['challenge', 'survived', 'days', 'only'].includes(k)),
      isMinecraft: keywords.includes('minecraft'),
      isSurvival: keywords.includes('survived') || keywords.includes('survival'),
      isHardcore: keywords.includes('hardcore'),
      hasDuration: keywords.some(k => k.match(/\d+/))
    };

    // Return the same analysis object structure
    if (contentType.isMinecraft && contentType.isSurvival) {
      return {
        title: "Análise Detalhada - Minecraft Survival Content",
        mainInsight: "Seu conteúdo de gaming está performando bem! Algumas sugestões:",
        quickTips: [
          "Crie séries com episódios regulares para manter o público engajado",
          "Explore diferentes modos de jogo e desafios para variar o conteúdo",
          "Interaja com outros criadores de conteúdo de gaming para colaborações",
          "Use timestamps nos vídeos longos para melhor retenção"
        ],
        fullAnalysis: {
          seriesAndContinuity: [
            "Planeje episódios regulares (ideal: 2-3 por semana)",
            "Mantenha episódios entre 20-25 minutos",
            "Use thumbnails consistentes com numeração clara",
            "Crie um storyline para cada episódio"
          ],
          engagement: [
            "Incentive sugestões de desafios nos comentários",
            "Faça votações para decisões importantes",
            "Crie momentos de tensão para hooks",
            "Compartilhe highlights no TikTok/Reels"
          ],
          technical: [
            "Use timestamps para momentos importantes",
            "Otimize títulos com 'Minecraft Hardcore' no início",
            "Inclua tags relevantes: survival, hardcore, challenge",
            "Crie playlists organizadas por temporada"
          ]
        }
      };
    }

    return {
      title: "Análise Geral de Conteúdo",
      mainInsight: "Recomendações personalizadas para otimizar seu desempenho:",
      quickTips: [
        "Analise os horários de maior engajamento",
        "Experimente diferentes formatos",
        "Mantenha consistência visual",
        "Crie séries temáticas"
      ],
      fullAnalysis: {
        content: [
          "Defina um calendário de postagem",
          "Interaja com sua audiência",
          "Monitore tendências do nicho",
          "Adapte conteúdo que funciona"
        ]
      }
    };
  };

  const analysis = contentName ? getAIAnalysis(contentName) : null;

  return (
    <div className="space-y-6">
      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {insights.map((insight, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg border ${
              insight.type === 'success'
                ? 'bg-green-50 border-green-200'
                : insight.type === 'warning'
                ? 'bg-yellow-50 border-yellow-200'
                : 'bg-blue-50 border-blue-200'
            }`}
          >
            <div className="flex items-start space-x-3">
              {insight.type === 'success' ? (
                <TrendingUp className="h-5 w-5 text-green-600 mt-0.5" />
              ) : insight.type === 'warning' ? (
                <TrendingDown className="h-5 w-5 text-yellow-600 mt-0.5" />
              ) : (
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              )}
              <div>
                <p className={`text-sm ${
                  insight.type === 'success'
                    ? 'text-green-800'
                    : insight.type === 'warning'
                    ? 'text-yellow-800'
                    : 'text-blue-800'
                }`}>
                  {insight.message}
                </p>
                {insight.improvement && (
                  <p className={`text-xs mt-1 ${
                    insight.type === 'success'
                      ? 'text-green-600'
                      : insight.type === 'warning'
                      ? 'text-yellow-600'
                      : 'text-blue-600'
                  }`}>
                    {insight.improvement > 0 ? '+' : ''}{insight.improvement.toFixed(1)}% de variação
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Compact AI Analysis */}
      {analysis && (
        <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Brain className="h-5 w-5 text-indigo-600" />
              <p className="text-sm text-indigo-700">{analysis.mainInsight}</p>
            </div>
            <button
              onClick={() => setShowDetailedModal(true)}
              className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
            >
              Ver análise completa
            </button>
          </div>
        </div>
      )}

      {/* Detailed Analysis Modal */}
      {showDetailedModal && analysis && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <Brain className="h-6 w-6 text-indigo-600" />
                <h3 className="text-xl font-semibold text-gray-900">{analysis.title}</h3>
              </div>
              <button
                onClick={() => setShowDetailedModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              <div className="bg-indigo-50 p-4 rounded-lg">
                <p className="text-indigo-700 font-medium">{analysis.mainInsight}</p>
                <ul className="mt-4 space-y-2">
                  {analysis.quickTips.map((tip, index) => (
                    <li key={index} className="text-indigo-600 flex items-start">
                      <span className="mr-2">•</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

              {Object.entries(analysis.fullAnalysis).map(([section, tips], index) => (
                <div key={section} className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3 capitalize">
                    {section.replace(/([A-Z])/g, ' $1').trim()}
                  </h4>
                  <ul className="space-y-2">
                    {tips.map((tip, tipIndex) => (
                      <li key={tipIndex} className="text-gray-600 flex items-start">
                        <span className="text-indigo-500 mr-2">•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              <div className="text-sm text-indigo-600 pt-4 border-t border-gray-100">
                Estas recomendações são baseadas na análise do seu conteúdo, métricas de desempenho e tendências do mercado.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}